import { ChangeDetectionStrategy, ChangeDetectorRef, Component, DestroyRef, Input, OnInit, effect, inject, input } from '@angular/core';
import { CommonModule } from '@angular/common';
import { AbstractControl, FormBuilder, FormGroup, FormControl, FormsModule, ReactiveFormsModule } from '@angular/forms';
import { Store } from '@ngxs/store';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { distinctUntilChanged, filter, switchMap, startWith, map, shareReplay } from 'rxjs/operators';
import { combineLatest, from, Observable, of, tap } from 'rxjs';
import { ConsumerEntityService, ConsumerEntityState } from '../services/consumer.entity.service';
import { ConsumerEntity, SavedLearnObjectType } from '../consumer.entity';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';
import { MatCheckboxModule } from '@angular/material/checkbox';
import { MatSelectModule } from '@angular/material/select';
import { MatSlideToggleModule } from '@angular/material/slide-toggle';
import { MatExpansionModule } from '@angular/material/expansion';
import { MatIconModule } from '@angular/material/icon';
import { MatButtonModule } from '@angular/material/button';
import { GlobalMetaDataEntity, MetadataField } from '../../global-metadata/global-metadata.entity';
import { MetadataFieldDataTypes } from '../../core/types';
import { TranslocoModule } from '@jsverse/transloco';
import { GenericTreeService } from '../../generic-tree/services/generic-tree.entity.service';
import { GenericTreeEntity } from '../../generic-tree/generic-tree.entity';
import { SubjectEntity } from '../../subject/subject.entity';
import { GlobalMetaDataEntityService } from '../../global-metadata/services/global-metadata.entity.service';

@Component({
  selector: 'pixl-object-consumer-metadata',
  templateUrl: './object-consumer-metadata.component.html',
  styleUrl: './object-consumer-metadata.component.scss',
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    ReactiveFormsModule,
    MatFormFieldModule,
    MatInputModule,
    MatCheckboxModule,
    MatSelectModule,
    MatSlideToggleModule,
    MatExpansionModule,
    TranslocoModule,
    MatIconModule,
    MatButtonModule
  ],
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class ObjectConsumerMetadataComponent implements OnInit {
  @Input({ required: true }) parentForm!: FormGroup;
  @Input() objectType = '';
  @Input() consumerId: string | null = null;
  customMetadata = input<GlobalMetaDataEntity[] | null>(null);
  isConsumerField = input<boolean>(false);

  private store = inject(Store);
  private destroyRef = inject(DestroyRef);
  private fb = inject(FormBuilder);
  private cdr = inject(ChangeDetectorRef);

  private consumerService = inject(ConsumerEntityService);
  private genericTreeService = inject(GenericTreeService);
  private globalMetaDataService = inject(GlobalMetaDataEntityService);

  consumer$: Observable<ConsumerEntity | null> = of(null);
  globalMetadata: GlobalMetaDataEntity[] = [];
  competences$: Observable<{ entity: GenericTreeEntity, level: number }[]> = of([]);
  subjects$: Observable<SubjectEntity[]> = of([]);

  subjectFilterControl = new FormControl<string | null>(null);
  pairedSelectionFilterControls = new Map<string, FormControl<string[] | null>>();

  // Object types array from consumer
  objectTypeMetadata: SavedLearnObjectType[] = [];
  globalFields: MetadataField[] = [];
  objectTypeFields: MetadataField[] = [];
  dataTypes = MetadataFieldDataTypes;

  constructor() {
    effect(() => {
      const custom = this.customMetadata();
      if (custom !== null && this.parentForm) {
        this.globalMetadata = custom;
        this.objectTypeMetadata = [];
        this.syncFormGroup();
      }
    });
  }

  ngOnInit() {
    this.ensureConsumerDataGroup();

    // Custom mode is fully handled by the effect in the constructor
    if (this.customMetadata() !== null) {
      return;
    }

    // Find the relevant consumer control (can be 'consumer', 'consumers', or 'consumerId')
    const consumerControl = this.parentForm.get('consumer') ||
      this.parentForm.get('consumers') ||
      this.parentForm.get('consumerId');

    const getVal = (val: unknown) => {
      if (Array.isArray(val)) return val[0];
      return val;
    };

    const initialConsumerId = this.consumerId || getVal(consumerControl?.value);

    this.consumer$ = (consumerControl ?
      consumerControl.valueChanges.pipe(
        startWith(initialConsumerId),
        map(getVal)
      ) :
      of(this.consumerId)).pipe(
      takeUntilDestroyed(this.destroyRef),
      distinctUntilChanged(),
      filter(id => !!id),
      switchMap(id => {
        this.loadCompetences(id);

        return from(this.consumerService.ensureSingleLoaded(id)).pipe(
          switchMap(() => this.store.select(ConsumerEntityState.byId).pipe(map(fn => fn(id))))
        );
      })
    ) as Observable<ConsumerEntity | null>;

    this.consumer$.subscribe(consumer => {
      if (consumer) {
        if (this.isConsumerField()) {
          // Load ALL GlobalMetadata with isConsumerField=true from the store/API
          const query = { 'filter[isConsumerField]': 1, 'page[size]': 1000 };
          this.globalMetaDataService.ensureLoaded(query).then(() => {
            const allMeta = this.store.selectSnapshot(
              this.globalMetaDataService.allByQuery(query)
            );
            this.globalMetadata = allMeta.filter(m => !!m.isConsumerField);
            this.objectTypeMetadata = [];

            // Pre-populate consumerData group with existing saved values
            const existingData = consumer.consumerData || {};
            const group = this.consumerDataGroup;
            if (group) {
              Object.keys(existingData).forEach(key => {
                if (!group.get(key)) {
                  group.addControl(key, this.fb.control(existingData[key]));
                }
                else {
                  group.get(key)?.setValue(existingData[key], { emitEvent: false });
                }
              });
            }

            setTimeout(() => this.syncFormGroup(), 100);
          });
        }
        else {
          this.globalMetadata = consumer.consumerMetadata || [];
          this.objectTypeMetadata = (consumer.learnObjectTypeDescriptions || [])
            .filter(t => t.label === this.objectType);
          setTimeout(() => this.syncFormGroup(), 100);
        }
      }
      else {
        this.globalMetadata = [];
        this.objectTypeMetadata = [];
        this.syncFormGroup();
      }
    });
  }

  private loadCompetences(consumerId: string) {
    const query = { 'filter[consumerId]': consumerId, 'page[size]': 1000 };
    this.genericTreeService.ensureLoaded(query);

    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const rawCompetences$ = this.store.select((state: any) => state.genericTrees).pipe(
      map(state => {
        const all = Object.values(state?.entities || {}) as GenericTreeEntity[];
        return all.filter(node => node.consumerId === consumerId);
      }),
      shareReplay(1)
    );

    // 2. Derive available subjects from the loaded competences
    this.subjects$ = combineLatest([
      rawCompetences$,
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      this.store.select((state: any) => state.subjectEntity)
    ]).pipe(
      map(([nodes, subjectState]) => {
        if (!nodes.length || !subjectState?.entities) {
          return [];
        }

        const validIds = new Set(
          // eslint-disable-next-line @typescript-eslint/no-explicit-any
          nodes.map(n => (n as any).subjectId).filter(id => !!id)
        );

        // 3. Map to the actual entity objects
        const foundSubjects = Object.values(subjectState.entities)
          // eslint-disable-next-line @typescript-eslint/no-explicit-any
          .filter((s: any) => validIds.has(s.id)) as SubjectEntity[];

        return foundSubjects;
      }),
      tap(() => this.cdr.markForCheck())
    );

    // 3. Create the filtered list for the dropdown
    this.competences$ = combineLatest([
      rawCompetences$,
      this.subjectFilterControl.valueChanges.pipe(
        startWith(this.subjectFilterControl.value)
      )
    ]).pipe(
      map(([nodes, selectedSubjectId]) => {
        const filteredNodes = selectedSubjectId ?
          // eslint-disable-next-line @typescript-eslint/no-explicit-any
          (nodes as any[]).filter(n => (n as any).subjectId === selectedSubjectId) :
          nodes as GenericTreeEntity[];

        // 2. Map and Sort
        const levelMap = this.genericTreeService.calculateNodeLevels(filteredNodes);
        return filteredNodes.map(entity => ({
          entity,
          level: levelMap.get(entity.id) || 1
        })).sort((a, b) => (a.entity.sortOrder ?? 0) - (b.entity.sortOrder ?? 0));
      }),
      tap(() => {
        this.cdr.markForCheck();
      })
    );
  }

  private ensureConsumerDataGroup() {
    if (this.parentForm && !this.parentForm.get('consumerData')) {
      this.parentForm.addControl('consumerData', this.fb.group({}));
    }
  }

  get consumerDataGroup(): FormGroup {
    return this.parentForm?.get('consumerData') as FormGroup;
  }

  getGroup(parent: AbstractControl, name: string): FormGroup {
    return parent.get(name) as FormGroup;
  }

  get controlKeys(): string[] {
    if (!this.consumerDataGroup?.controls) {
      return [];
    }
    return Object.keys(this.consumerDataGroup.controls);
  }

  private buildControl(field: MetadataField, initialValue: unknown): AbstractControl {
    if (field.datatype === this.dataTypes.Selection || field.datatype === this.dataTypes.PairedSelection) {
      let val = initialValue;
      if (val && !Array.isArray(val)) {
        val = [val];
      }
      else if (!val) {
        val = [];
      }
      return new FormControl(JSON.parse(JSON.stringify(val)));
    }

    let val = initialValue;
    if (val !== null && (field.datatype === this.dataTypes.UnsignedIntStepper || field.datatype === this.dataTypes.Int)) {
      val = parseInt(val as string, 10);
      if (isNaN(val as number)) {
        val = null;
      }
    }
    else if (val !== null && field.datatype === this.dataTypes.Float) {
      val = parseFloat(val as string);
      if (isNaN(val as number)) {
        val = null;
      }
    }
    const control = new FormControl(val);

    if (field.datatype === this.dataTypes.UnsignedIntStepper || field.datatype === this.dataTypes.Int || field.datatype === this.dataTypes.Float) {
      control.valueChanges.pipe(takeUntilDestroyed(this.destroyRef)).subscribe(v => {
        if (typeof v === 'string' && v !== '') {
          const num = field.datatype === this.dataTypes.Float ? parseFloat(v) : parseInt(v, 10);
          if (!isNaN(num)) {
            control.setValue(num, { emitEvent: false });
          }
        }
      });
    }
    return control;
  }

  private syncFormGroup() {
    this.ensureConsumerDataGroup();
    const group = this.consumerDataGroup;
    if (!group) return;

    const globalFieldsMap = new Map<string, MetadataField>();
    const objectTypeFieldsMap = new Map<string, MetadataField>();

    const rawData = group.getRawValue() || {};
    const parentData = this.parentForm.get('consumerData')?.value || {};
    const mergedData = { ...parentData, ...rawData };

    /**
     * Internal helper to sync specific fields into the form group
     */
    const syncFields = (fields: MetadataField[], isGlobal: boolean) => {
      fields.forEach((field) => {
        if (isGlobal) globalFieldsMap.set(field.name, field);
        else objectTypeFieldsMap.set(field.name, field);

        let initialValue = mergedData[field.name] ?? null;

        // Normalization for Selection / PairedSelection Type (Object -> Array)
        if (field.datatype === this.dataTypes.Selection || field.datatype === this.dataTypes.PairedSelection) {
          if (initialValue && !Array.isArray(initialValue)) {
            initialValue = [initialValue];
          }
          else if (!initialValue) {
            initialValue = [];
          }
        }

        if (!group.contains(field.name)) {
          group.addControl(field.name, this.buildControl(field, initialValue));
        }
        else {
          const control = group.get(field.name);
          const currentValStr = JSON.stringify(control?.value);
          const initialValStr = JSON.stringify(initialValue);

          if (control && currentValStr !== initialValStr) {
            const finalVal = (typeof initialValue === 'object' && initialValue !== null) ?
              JSON.parse(initialValStr) :
              initialValue;
            control.setValue(finalVal, { emitEvent: false });
          }
        }
      });
    };

    // 1. Process Global Metadata — filter by isConsumerField when in consumer-field mode
    const isCustom = this.customMetadata() !== null;
    this.globalMetadata
      .filter(meta => isCustom || (this.isConsumerField() ? !!meta.isConsumerField : !meta.isConsumerField))
      .forEach(meta => {
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        const fields = meta.fieldConfig || (meta as any).fieldConf;
        if (fields) syncFields(fields, true);
      });

    // 2. Process Object Type Metadata — skip when in consumer-field mode or custom mode
    if (!this.isConsumerField() && !isCustom) {
      this.objectTypeMetadata.forEach(typeDesc => {
        if (typeDesc.metadata) {
          typeDesc.metadata.forEach(meta => {
            // eslint-disable-next-line @typescript-eslint/no-explicit-any
            const fields = meta.fieldConfig || (meta as any).fieldConf;
            if (fields) syncFields(fields, false);
          });
        }
      });
    }

    this.globalFields = Array.from(globalFieldsMap.values());
    this.objectTypeFields = Array.from(objectTypeFieldsMap.values());
    this.cdr.markForCheck();
  }

  toJson(value: unknown): string {
    return value ? JSON.stringify(value, null, 2) : '';
  }

  fromJson(group: AbstractControl, key: string, value: string): void {
    try {
      const parsed = value ? JSON.parse(value) : null;
      group.get(key)?.setValue(parsed);
    }
    catch {
      group.get(key)?.setValue(value);
    }
  }

  typeOf(value: unknown): string {
    return typeof value;
  }

  trackByKey(index: number, item: MetadataField | string): string {
    return typeof item === 'string' ? item : (item.name || item.label || String(index));
  }

  getControl(parent: AbstractControl, name: string): FormControl {
    return parent.get(name) as FormControl;
  }

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  getSelectionValue(control: AbstractControl): any[] {
    if (!control) {
      return [];
    }

    if (!Array.isArray(control.value)) {
      return [];
    }

    return control.value;
  }

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  consoleLogSelection(fieldName: string, value: any) {
    console.log(`[Selection UI Render] Field: ${fieldName}`, {
      value,
      type: typeof value,
      isArray: Array.isArray(value),
      length: value?.length
    });
    return ''; // Return empty string so nothing renders in UI
  }

  getSrcOption(options: MetadataField[], name: string): MetadataField | undefined {
    return options?.find(o => o.name === name);
  }

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  getAvailableOptions(options: MetadataField[], value: any[]): MetadataField[] {
    const valArr = Array.isArray(value) ? value : [];
    return (options || []).filter(o => !valArr.some(v => v.name === o.name));
  }

  getSecondaryLabelFilterControl(fieldName: string): FormControl<string[] | null> {
    if (!this.pairedSelectionFilterControls.has(fieldName)) {
      this.pairedSelectionFilterControls.set(fieldName, new FormControl<string[] | null>(null));
    }
    return this.pairedSelectionFilterControls.get(fieldName)!;
  }

  getUniqueSecondaryLabels(options: MetadataField[]): string[] {
    const labels = (options || [])
      .map(o => o.secondaryLabel)
      .filter((l): l is string => !!l);
    return [...new Set(labels)];
  }

  getFilteredPairedOptions(options: MetadataField[], filterValue: string[] | string | null): MetadataField[] {
    if (!filterValue || (Array.isArray(filterValue) && filterValue.length === 0)) {
      return options || [];
    }
    const filters = Array.isArray(filterValue) ? filterValue : [filterValue];
    return (options || []).filter(o => o.secondaryLabel != null && filters.includes(o.secondaryLabel));
  }

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  addSelection(control: AbstractControl, parentName: string | undefined, selectedOpt: MetadataField, selectElement: any) {
    const fullTree = JSON.parse(JSON.stringify(control.value || []));
    const newItem: Record<string, unknown> = {
      name: selectedOpt.name,
      label: selectedOpt.label || selectedOpt.name,
      subFieldConf: []
    };
    if (selectedOpt.secondaryLabel) {
      newItem.secondaryLabel = selectedOpt.secondaryLabel;
    }

    if (!parentName) {
      fullTree.push(newItem);
    }
    else {
      this.findAndPushByName(fullTree, parentName, newItem);
    }

    control.setValue(fullTree);
    control.markAsDirty();
    if (selectElement) selectElement.value = null;
    this.cdr.markForCheck();
  }

  addAllFilteredSelections(control: AbstractControl, parentName: string | undefined, options: MetadataField[], filterValue: string[] | string | null): void {
    const currentValue = this.getSelectionValue(control);
    const filtered = this.getFilteredPairedOptions(options, filterValue);
    const available = this.getAvailableOptions(filtered, currentValue);

    const fullTree = JSON.parse(JSON.stringify(control.value || []));

    for (const opt of available) {
      const newItem: Record<string, unknown> = {
        name: opt.name,
        label: opt.label || opt.name,
        subFieldConf: []
      };
      if (opt.secondaryLabel) {
        newItem.secondaryLabel = opt.secondaryLabel;
      }

      if (!parentName) {
        fullTree.push(newItem);
      }
      else {
        this.findAndPushByName(fullTree, parentName, newItem);
      }
    }

    control.setValue(fullTree);
    control.markAsDirty();
    this.cdr.markForCheck();
  }

  removeAllSelections(control: AbstractControl): void {
    control.setValue([]);
    control.markAsDirty();
    this.cdr.markForCheck();
  }

  // Remove an item
  removeSelection(control: AbstractControl, parentName: string | undefined, index: number) {
    const fullTree = JSON.parse(JSON.stringify(control.value || []));

    if (!parentName) {
      fullTree.splice(index, 1);
    }
    else {
      this.findAndRemoveByName(fullTree, parentName, index);
    }

    control.setValue(fullTree);
    control.markAsDirty();
    this.cdr.markForCheck();
  }

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  private findAndPushByName(list: any[], targetName: string, newItem: any): boolean {
    for (const item of list) {
      if (item.name === targetName) {
        item.subFieldConf = item.subFieldConf || [];
        item.subFieldConf.push(newItem);
        return true;
      }
      if (item.subFieldConf && this.findAndPushByName(item.subFieldConf, targetName, newItem)) {
        return true;
      }
    }
    return false;
  }

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  private findAndRemoveByName(list: any[], targetName: string, index: number): boolean {
    for (const item of list) {
      if (item.name === targetName) {
        item.subFieldConf.splice(index, 1);
        return true;
      }
      if (item.subFieldConf && this.findAndRemoveByName(item.subFieldConf, targetName, index)) {
        return true;
      }
    }
    return false;
  }

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  getSubFieldConfSafe(item: any): any[] {
    if (!item || !item.subFieldConf) {
      return [];
    }
    return item.subFieldConf;
  }
}
